import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  computed,
  inject,
  Injectable,
  type OnDestroy,
  signal,
} from '@angular/core';
import { Subject } from 'rxjs';

import type { ToastConfig } from '@/shared/models/';

/**
 * A toast that never dismisses itself. The viewer closes it, which `closable` allows by default.
 * Use it only where the message carries information the viewer has to act on - an ordinary
 * confirmation should still fade.
 */
export const TOAST_DURATION_PERSISTENT = 0;

@Injectable({
  providedIn: 'root',
})
export class ToastService implements OnDestroy {
  readonly #overlay = inject(Overlay);
  #overlayRef: OverlayRef | null = null;

  readonly #minDuration = 5000;
  readonly #maxDuration = 30_000;
  readonly #defaultToastConfig: ToastConfig<unknown> = {
    closable: true,
    duration: this.#minDuration,
    type: 'info',
  };

  readonly #timeouts = new Map<string, ReturnType<typeof setTimeout>>([]);
  readonly #resultSubjects = new Map<string, Subject<unknown>>([]);
  readonly #toasts = signal<Partial<ToastConfig<unknown>>[]>([]);

  readonly #toastsWithIdAndSettings = computed<ToastConfig<unknown>[]>(() =>
    this.#toasts().map(toast => ({
      ...this.#defaultToastConfig,
      ...toast,
      duration:
        toast.duration === TOAST_DURATION_PERSISTENT
          ? TOAST_DURATION_PERSISTENT
          : Math.min(
              this.#maxDuration,
              Math.max(this.#minDuration, toast.duration ?? 0)
            ),
      id: toast.id ?? crypto.randomUUID(),
    }))
  );
  readonly toasts = computed<ToastConfig<unknown>[]>(() =>
    this.#toastsWithIdAndSettings().filter(
      (value, index, array) => array.indexOf(value) === index
    )
  );

  ngOnDestroy(): void {
    for (const subject of this.#resultSubjects.values()) {
      subject.complete();
    }
    this.#overlayRef?.dispose();
  }

  /**
   * Lazily creates a CDK Overlay and attaches the ToastRendererComponent.
   * Uses a dynamic import to avoid circular dependency
   * (ToastRendererComponent -> ToastService -> ToastRendererComponent).
   */
  async #ensureOverlay(): Promise<void> {
    if (this.#overlayRef) return;

    this.#overlayRef = this.#overlay.create({
      hasBackdrop: false,
      // there is a custom css class for the toast overlay to make it on top of the modals
      // see components.css
      panelClass: ['toast-overlay'],
      positionStrategy: this.#overlay
        .position()
        .global()
        .top('0.25rem')
        .right('0.25rem'),
    });

    const { ToastRendererComponent } =
      await import('@/shared/components/feedback/toast-renderer/toast-renderer');
    this.#overlayRef.attach(new ComponentPortal(ToastRendererComponent));
  }

  /*
    Add an entry to the list of rendered toasts.
    Prevent multiple identical toasts by setting the optional property `id` in
    the supplied config. If the ID is set, and the same ID is already being
    rendered, no additional toast will appear.
    Returns
  */
  show(
    message: string,
    config: Partial<ToastConfig<unknown>>
  ): Subject<unknown> {
    const id = config.id ?? crypto.randomUUID();
    // dirty hack to ensure the overlay is created before the toast is shown
    // allows the subject to be created before the overlay is created
    setTimeout(async () => {
      this.#ensureOverlay();

      this.#toasts.update(state => [
        ...state,
        {
          ...config,
          id,
          message: message,
        },
      ]);

      this.#setupTimeout(id);
    }, 0);

    const resultSubject = new Subject();
    this.#resultSubjects.set(id, resultSubject);
    return resultSubject;
  }

  /*
    Remove a specific entry from the list of rendered toasts.
  */
  hide(toastId: string, value: unknown): void {
    this.#clearTimeout(toastId);
    this.#removeToast(toastId);
    this.#emitAndComplete(toastId, value);
  }

  #setupTimeout(toastId: string): void {
    const toast = this.toasts().find(t => t.id === toastId);

    if (!toast || toast.duration === TOAST_DURATION_PERSISTENT) {
      return;
    }

    const timeoutId = setTimeout(
      () => this.#removeToast(toast!.id!),
      toast!.duration
    );
    this.#timeouts.set(toastId, timeoutId);
  }

  #clearTimeout(toastId: string): void {
    if (this.#timeouts.has(toastId)) {
      clearTimeout(this.#timeouts.get(toastId)!);
      this.#timeouts.delete(toastId);
    }
  }

  #removeToast(toastId: string): void {
    this.#toasts.update(state => state.filter(toast => toast.id !== toastId));
  }

  #emitAndComplete(toastId: string, value: unknown): void {
    if (!this.#resultSubjects.has(toastId)) {
      return;
    }
    const result = this.#resultSubjects.get(toastId);
    result!.next(value);
    result!.complete();
    this.#resultSubjects.delete(toastId);
  }
}
